<template>
    <div v-if="sla">
        <div class="mb-2">
            <div class="text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                {{ trans('maintainers.sla.contracting_authority.title') }}
            </div>
            <div>{{ sla.contractingAuthority?.name || '-' }}</div>
        </div>

        <div v-if="sla.supportSchedules?.length">
            <div v-for="schedule in sla.supportSchedules" :key="schedule.name" class="mb-3">
                <div class="mb-1 text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                    {{ schedule.name }}
                </div>
                <div v-if="getScheduleContacts(schedule).length" class="mb-2">
                    <div v-for="contact in getScheduleContacts(schedule)" :key="contact.id" class="mb-1">
                        <div class="flex items-center gap-2">
                            <span>{{ contact.name }}</span>
                            <a v-if="hasSelectedPhone(contact)" :href="'tel:' + contact.phone" class="text-primary" @click.stop="">
                                <icon name="cil-phone" />
                            </a>
                            <a v-if="hasSelectedEmail(contact)" :href="'mailto:' + contact.email" class="text-primary" @click.stop="">
                                <icon name="cil-envelope-closed" />
                            </a>
                        </div>
                        <div v-if="contact.role" class="text-sm text-gray-500">{{ contact.role }}</div>
                    </div>
                </div>
                <div class="mb-2">
                    <div class="mb-1 text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                        {{ trans('maintainers.sla.working_hours.title') }}
                    </div>
                    <div class="text-gray-500">
                        {{ getScheduleWorkingHoursLabel(schedule) }}
                    </div>
                </div>
                <div class="flex gap-6">
                    <div>
                        <div class="mb-1 text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                            {{ trans('maintainers.sla.time_to_respond.title') }}
                        </div>
                        <div v-for="priority in priorities" :key="'respond-' + priority" class="flex items-center gap-2">
                            <work-order-priority :priority="getPriorityLabel(priority)" icon-only />
                            <span class="text-gray-500">{{ getTimeDurationLabel(schedule.resolutionPriorities?.timeToRespond?.[priority]) }}</span>
                        </div>
                    </div>
                    <div>
                        <div class="mb-1 text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                            {{ trans('maintainers.sla.time_to_resolve.title') }}
                        </div>
                        <div v-for="priority in priorities" :key="'resolve-' + priority" class="flex items-center gap-2">
                            <work-order-priority :priority="getPriorityLabel(priority)" icon-only />
                            <span class="text-gray-500">{{ getTimeDurationLabel(schedule.resolutionPriorities?.timeToResolve?.[priority]) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="mb-2">
            <div class="text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                {{ trans('maintainers.sla.agreement_term.title') }}
            </div>
            <div class="flex gap-2">
                <span class="text-gray-500">{{ trans('maintainers.sla.agreement_term.from.title') }}</span>
                <span v-if="sla.startDate" class="whitespace-nowrap">{{ formatDate(sla.startDate) }}</span>
                <span v-else class="text-gray-500">{{ trans('general.not_available') }}</span>
            </div>
            <div class="flex gap-2">
                <span class="text-gray-500">{{ trans('maintainers.sla.agreement_term.to.title') }}</span>
                <span v-if="sla.endDate" :class="sla.expired || sla.expiresSoon ? 'text-red-600' : ''" class="whitespace-nowrap">
                    {{ formatDate(sla.endDate) }}
                </span>
                <span v-else class="text-gray-500">{{ trans('general.not_available') }}</span>
            </div>
        </div>
    </div>
</template>
<script setup>
import WorkOrderPriority from '@/Pages/WorkOrders/Modals/WorkOrderPriority.vue';
import { trans } from '@/mixins/i18n.js';
import { getWeekday } from '@/Pages/Stakeholders/Maintainers/weekdays.js';
import fullDate from '@/filters/fullDate';
import { usePage } from '@inertiajs/vue3';
import { DateTime } from 'luxon';
import { computed } from 'vue';

const page = usePage();

const priorities = ['high', 'medium', 'low'];

const weekdayOrder = computed(() => (page.props.weekdays?.data ?? []).map((weekday) => weekday.value));

const getPriorityLabel = (priority) =>
    page.props.priorities?.data?.find((el) => el.value === priority) || {
        value: priority,
        label: priority,
    };

const props = defineProps({
    sla: Object,
    supportContacts: Array,
});

const getScheduleContacts = (schedule) => {
    if (!schedule.contacts?.length || !props.supportContacts?.length) {
        return [];
    }

    const mergedSelections = schedule.contacts.reduce((map, selection) => {
        const channelTypes = map.get(selection.contactId) ?? new Set();
        (selection.channelTypes ?? []).forEach((type) => channelTypes.add(type));
        map.set(selection.contactId, channelTypes);

        return map;
    }, new Map());

    return [...mergedSelections.entries()]
        .map(([contactId, channelTypes]) => {
            const contact = props.supportContacts.find((item) => item.id === contactId);

            return contact ? { ...contact, channelTypes: [...channelTypes] } : null;
        })
        .filter(Boolean);
};

const hasSelectedPhone = (contact) => Boolean(contact.phone) && (contact.channelTypes.includes('voice') || contact.channelTypes.includes('sms'));

const hasSelectedEmail = (contact) => Boolean(contact.email) && contact.channelTypes.includes('email');

const getTimeDurationLabel = (duration) => {
    if (!duration?.value) {
        return '-';
    }

    return `${duration.value} ${trans(`general.${duration.unit}`)}`;
};

const formatTime = (time) => (typeof time === 'string' ? time.slice(0, 5) : time);

const formatDaysLabel = (days) => {
    const order = weekdayOrder.value;
    const sorted = [...days].sort((a, b) => order.indexOf(a) - order.indexOf(b));
    const indexes = sorted.map((day) => order.indexOf(day));
    const isConsecutive = indexes.length > 1 && indexes.every((value, index) => index === 0 || value === indexes[index - 1] + 1);
    const locale = page.props.locale;

    if (isConsecutive) {
        return `${getWeekday(sorted[0], locale)?.label} - ${getWeekday(sorted[sorted.length - 1], locale)?.label}`;
    }

    return sorted.map((day) => getWeekday(day, locale)?.label).join(', ');
};

const getScheduleWorkingHoursLabel = (schedule) => {
    if (schedule.isTwentyFourSeven) {
        return trans('maintainers.sla.working_hours.24_7');
    }

    if (!schedule.days?.length) {
        return trans('general.not_available');
    }

    const daysLabel = formatDaysLabel(schedule.days);
    const timeRange = `${formatTime(schedule.startTime)} - ${formatTime(schedule.endTime)}`;

    return schedule.timezone ? `${daysLabel}, ${timeRange} (${schedule.timezone})` : `${daysLabel}, ${timeRange}`;
};

const formatDate = (dateString) => {
    const date = DateTime.fromFormat(dateString, 'y-MM-dd');

    return fullDate(date, page.props.locale);
};
</script>
